import { isProtocolManifest, protocolManifestPath } from '@hub/common';
import { storageObjectUrl } from 'features/firebase/storageObjectUrl';
import { logger } from 'features/logger/logger';
import { isNavigatingAway } from 'shared/utils/pageLeaveSignal';
import type { DbProtocolManifest } from '@hub/common';

/** The time limit of each start-asset request that sets one. */
export const PREFETCH_TIMEOUT_MS = 15_000;

export const protocolManifestUrl = (protocolId: string): string =>
  storageObjectUrl(protocolManifestPath(protocolId));

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
};

const warnNoManifest = (protocolId: string, error: unknown): null => {
  if (!isNavigatingAway())
    logger.warn('Unable to load the protocol manifest', {
      details: { protocolId, error },
    });
  return null;
};

// Resolves `null` for a missing manifest or a body that is not one; rejects
// on a network error, a timeout or another non-OK status.
const fetchProtocolManifest = async (
  protocolId: string
): Promise<DbProtocolManifest | null> => {
  const response = await fetch(protocolManifestUrl(protocolId), {
    signal: AbortSignal.timeout(PREFETCH_TIMEOUT_MS),
  });
  const failure = (): Error =>
    new Error(
      `Protocol manifest request failed with ${response.status.toString()}`
    );
  if (response.status === 404) return warnNoManifest(protocolId, failure());
  if (!response.ok) throw failure();
  const body = parseJson(await response.text());
  return isProtocolManifest(body)
    ? body
    : warnNoManifest(protocolId, new Error('Not a protocol manifest'));
};

const manifests = new Map<string, Promise<DbProtocolManifest | null>>();

/** The protocol's public manifest: one request per protocol at a time. A
 *  manifest, a 404 and a body that is not a manifest are kept for the page
 *  load; any other failure gives `null` and is requested again by the next
 *  call. A protocol id containing `/` gives `null` without a request or a
 *  warning. Never rejects. */
export const getProtocolManifest = (
  protocolId: string
): Promise<DbProtocolManifest | null> => {
  if (protocolId.includes('/')) return Promise.resolve(null);
  let manifest = manifests.get(protocolId);
  if (manifest === undefined) {
    manifest = fetchProtocolManifest(protocolId).catch((error: unknown) => {
      manifests.delete(protocolId);
      return warnNoManifest(protocolId, error);
    });
    manifests.set(protocolId, manifest);
  }
  return manifest;
};

export const forgetProtocolManifests = (): void => {
  manifests.clear();
};
